import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AccountingNumberModel, ArticleCreateData, ArticleModel, CategoryModel, EmployeePositionModel, GetDealsPipelineTypesResponse, GetEducationTypesResponse, initAccountingNumberModel, initCategoryModel, initEmployeePositionModel, initParticipantRoleModel, initPaymentMethodModel, initSimpleReferenceModel, initSourceModel, initUnitModel, PartnerTypeModel, ParticipantRoleModel, PaymentMethodModel, ReferenceDataSliceModel, SimpleReferenceModel, SourceModel, UnitModel, AbsenceTypeModel, AbsenceTypeRequest, TravelAllowance, TravelAllowanceList, TravelAllowanceCreateData, DriverTravelAllowance, DriverTravelAllowanceList, DriverTravelAllowanceCreateData } from "./reference-data-interface";
import { ErrorResponse, FilteredDropDownModelInit, FilteredDropDownRequest, FilteredDropDownResponse, ListModel, MainResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { API_BASE_URL as baseUri } from "config/env";

const initalValue: ReferenceDataSliceModel = {
    statusCode: '',
    message: '',
    showNotification: false,
    isLoading: false,
    isModalLoading: false,
    listOfEmployeePositions: [],
    employeePosition: null,
    listOfParticipantRoles: [],
    participantRole: null,
    listOfUnits: [],
    unit: null,
    listOfSources: [],
    source: null,
    listOfAccountingNumbers: [],
    accountingNumber: null,
    listOfCateogires: [],
    category: null,
    listOfPaymentMethods: [],
    paymentMethod: null,
    listOfIndustrySectors: [],
    industrySector: initSimpleReferenceModel,
    dealPipeline: initSimpleReferenceModel,
    educationType: initSimpleReferenceModel,
    listOfDealPipelines: [],
    listOfEducationTypes: [],
    listOfPartnerTypes: [],
    listOfAbsenceTypes: [],
    absenceType: initSimpleReferenceModel,
    partnerType: initSimpleReferenceModel,
    createData: {
        countries: [],
        sourceTypes: [],
        filteredContactsList: FilteredDropDownModelInit,
        filteredPartnersList: FilteredDropDownModelInit,
        projects: [],
        articleTypes: []
    },
    listOfActiveArticles: [],
    listOfInactiveArticles: [],
    article: null,
    listOfActiveTravelAllowances: [],
    listOfInactiveTravelAllowances: [],
    travelAllowance: null,
    travelAllowanceCreateData: null,
    listOfActiveDriverTravelAllowances: [],
    listOfInactiveDriverTravelAllowances: [],
    driverTravelAllowance: null,
    driverTravelAllowanceCreateData: null
}

//#region Employee position
export const GetAllEmployeePositions = createAsyncThunk<EmployeePositionModel[], void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAllEmployeePositions',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/employeePosition/getAll', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as EmployeePositionModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetEmployeePosition = createAsyncThunk<EmployeePositionModel, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetEmployeePosition',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/employeePosition/get?employeePositionId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as EmployeePositionModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateEmployeePosition = createAsyncThunk<string, EmployeePositionModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/UpdateEmployeePosition',
    async (model: EmployeePositionModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/employeePosition/update', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as string;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateEmployeePosition = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeactivateEmployeePosition',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/employeePosition/deactivate?employeePositionId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as string;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateEmployeePosition = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/ReactivateEmployeePosition',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/employeePosition/reactivate?employeePositionId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as string;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteEmployeePosition = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeleteEmployeePosition',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/employeePosition/delete?employeePositionId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as string;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateEmployeePosition = createAsyncThunk<string, EmployeePositionModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/CreateEmployeePosition',
    async (model: EmployeePositionModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/employeePosition/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);
//#endregion

//#region Units
export const GetAllUnits = createAsyncThunk<UnitModel[], void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAllUnits',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/unit/get', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UnitModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetUnit = createAsyncThunk<UnitModel, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetUnit',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/unit/get-by-id?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as UnitModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateUnit = createAsyncThunk<string, UnitModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/UpdateUnit',
    async (model: UnitModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/unit/update', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as string;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateUnit = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeactivateUnit',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/unit/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as string;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateUnit = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/ReactivateUnit',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/unit/reactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as string;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteUnit = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeleteUnit',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/unit/delete?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as string;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateUnit = createAsyncThunk<string, UnitModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/CreateUnit',
    async (model: UnitModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/unit/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);
//#endregion

//#region Source
export const GetAllSources = createAsyncThunk<SourceModel[], void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAllSources',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/source/getAll', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as SourceModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetSource = createAsyncThunk<SourceModel, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetSource',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/source/get?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as SourceModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateSource = createAsyncThunk<string, SourceModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/UpdateSource',
    async (model: SourceModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/source/update', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as string;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateSource = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeactivateSource',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/source/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as string;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateSource = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/ReactivateSource',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/source/reactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as string;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteSource = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeleteSource',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/source/delete?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as string;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateSource = createAsyncThunk<string, SourceModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/CreateSource',
    async (model: SourceModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/source/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetSourceCreateData = createAsyncThunk<{ sourceTypes: ListModel<number>[] }, void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetSourceCreateData',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/source/get-create-data', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as { sourceTypes: ListModel<number>[] };
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region Cateory

export const GetAllCategories = createAsyncThunk<CategoryModel[], void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAllCategories',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/category/getAll', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CategoryModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetCategory = createAsyncThunk<CategoryModel, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetCategory',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/category/get?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as CategoryModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateCategory = createAsyncThunk<string, CategoryModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/UpdateCategory',
    async (model: CategoryModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/category/update', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as string;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateCategory = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeactivateCategory',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/category/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as string;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateCategory = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/ReactivateCategory',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/category/reactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as string;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteCategory = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeleteCategory',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/category/delete?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as string;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateCategory = createAsyncThunk<string, CategoryModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/CreateCategory',
    async (model: CategoryModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/category/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

//#region Payment method

// get payment methods
export const GetAllPaymentMethods = createAsyncThunk<PaymentMethodModel[], void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAllPaymentMethods',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payment-method/getAll', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PaymentMethodModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// get payment method by ID
export const GetPaymentMethod = createAsyncThunk<PaymentMethodModel, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetPaymentMethod',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payment-method/get?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as PaymentMethodModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// update payment method
export const UpdatePaymentMethod = createAsyncThunk<string, PaymentMethodModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/UpdatePaymentMethod',
    async (model: PaymentMethodModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payment-method/update', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as string;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// deactivate payment method
export const DeactivatePaymentMethod = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeactivatePaymentMethod',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payment-method/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as string;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// reactivate payment method
export const ReactivatePaymentMethod = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/ReactivatePaymentMethod',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payment-method/reactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as string;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// delete payment method
export const DeletePaymentMethod = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeletePaymentMethod',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payment-method/delete?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as string;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// create payment method
export const CreatePaymentMethod = createAsyncThunk<string, PaymentMethodModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/CreatePaymentMethod',
    async (model: PaymentMethodModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/payment-method/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

//#region Accounting Number

export const GetAllAccountingNumbers = createAsyncThunk<AccountingNumberModel[], void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAllAccountingNumbers',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/accounting-number/getAll', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AccountingNumberModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAccountingNumber = createAsyncThunk<AccountingNumberModel, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAccountingNumber',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/accounting-number/get?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as AccountingNumberModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateAccountingNumber = createAsyncThunk<string, AccountingNumberModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/CreateAccountingNumber',
    async (model: AccountingNumberModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/accounting-number/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateAccountingNumber = createAsyncThunk<string, AccountingNumberModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/UpdateAccountingNumber',
    async (model: AccountingNumberModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/accounting-number/update', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as string;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateAccountingNumber = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeactivateAccountingNumber',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/accounting-number/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as string;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateAccountingNumber = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/ReactivateAccountingNumber',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/accounting-number/reactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as string;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteAccountingNumber = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeleteAccountingNumber',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/accounting-number/delete?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as string;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

//#region Participant role

export const GetAllParticipantRoles = createAsyncThunk<ParticipantRoleModel[], void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAllParticipantRoles',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/participant-roles/getall', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ParticipantRoleModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetParticipantRole = createAsyncThunk<ParticipantRoleModel, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetParticipantRole',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/participant-roles/get?participantRoleId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as ParticipantRoleModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateParticipantRole = createAsyncThunk<string, ParticipantRoleModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/UpdateParticipantRole',
    async (model: ParticipantRoleModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/participant-roles/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as string;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateParticipantRole = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeactivateParticipantRole',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/participant-roles/deactivate?participantRoleId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as string;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateParticipantRole = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/ReactivateParticipantRole',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/participant-roles/reactivate?participantRoleId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as string;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteParticipantRole = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeleteParticipantRole',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/participant-roles/delete?participantRoleId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as string;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateParticipantRole = createAsyncThunk<string, ParticipantRoleModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/CreateParticipantRole',
    async (model: ParticipantRoleModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/participant-roles/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

//#region Participant role

//#endregion

//#region Absence type

export const GetAllAbsenceTypes = createAsyncThunk<AbsenceTypeModel[], void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAllActiveAbsenceTypes',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/absence-type/getAll', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AbsenceTypeModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAbsenceType = createAsyncThunk<AbsenceTypeModel, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAbsenceType',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/absence-type/get?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as AbsenceTypeModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateAbsenceType = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeactivateAbsenceType',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/absence-type/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as string;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateAbsenceType = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/ReactivateAbsenceType',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/absence-type/reactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as string;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteAbsenceType = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeleteAbsenceType',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/absence-type/delete?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as string;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateAbsenceType = createAsyncThunk<string, AbsenceTypeRequest, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/CreateAbsenceType',
    async (model: AbsenceTypeRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/absence-type/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateAbsenceType = createAsyncThunk<string, AbsenceTypeRequest, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/UpdateAbsenceType',
    async (model: AbsenceTypeRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/absence-type/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as string;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

//#region PartnerType
export const GetAllPartnerTypes = createAsyncThunk<PartnerTypeModel[], void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAllPartnerTypes',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partner-type/getAll', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PartnerTypeModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPartnerType = createAsyncThunk<PartnerTypeModel, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetPartnerType',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partner-type/get?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as PartnerTypeModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdatePartnerType = createAsyncThunk<string, SimpleReferenceModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/UpdatePartnerType',
    async (model: CategoryModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partner-type/update', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as string;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivatePartnerType = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeactivatePartnerType',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partner-type/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as string;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivatePartnerType = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/ReactivatePartnerType',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partner-type/reactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as string;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeletePartnerType = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeletePartnerType',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partner-type/delete?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as string;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreatePartnerType = createAsyncThunk<string, SimpleReferenceModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/CreatePartnerType',
    async (model: SimpleReferenceModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partner-type/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);
//#endregion

//#region Industry sector
export const GetAllIndustrySectors = createAsyncThunk<SimpleReferenceModel[], void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAllIndustrySectors',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/industry-sector/getAll', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as SimpleReferenceModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetIndustrySector = createAsyncThunk<SimpleReferenceModel, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetIndustrySector',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/industry-sector/get?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as SimpleReferenceModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateIndustrySector = createAsyncThunk<string, SimpleReferenceModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/UpdateIndustrySector',
    async (model: CategoryModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/industry-sector/update', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as string;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateIndustrySector = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeactivateIndustrySector',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/industry-sector/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as string;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateIndustrySector = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/ReactivateIndustrySector',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/industry-sector/reactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as string;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteIndustrySector = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeleteIndustrySector',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/industry-sector/delete?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as string;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateIndustrySector = createAsyncThunk<string, SimpleReferenceModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/CreateIndustrySector',
    async (model: SimpleReferenceModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/industry-sector/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

//#region EducationType
export const GetAllEducationTypes = createAsyncThunk<GetEducationTypesResponse, void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAllEducationTypes',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/educationType/getAll', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetEducationTypesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetEducationType = createAsyncThunk<SimpleReferenceModel, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetEducationType',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/educationType/get?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as SimpleReferenceModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateEducationType = createAsyncThunk<string, SimpleReferenceModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/UpdateEducationType',
    async (model: CategoryModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/educationType/update', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as string;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateEducationType = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeactivateEducationType',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/educationType/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as string;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateEducationType = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/ReactivateEducationType',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/educationType/reactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as string;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteEducationType = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeleteEducationType',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/educationType/delete?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as string;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateEducationType = createAsyncThunk<string, SimpleReferenceModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/CreateEducationType',
    async (model: SimpleReferenceModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/educationType/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

//#region DealPipeline
export const GetAllDealPipelines = createAsyncThunk<GetDealsPipelineTypesResponse, void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAllDealPipelines',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/dealsPipelineType/getAll', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetDealsPipelineTypesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetDealPipeline = createAsyncThunk<SimpleReferenceModel, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetDealPipeline',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/dealsPipelineType/get?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as SimpleReferenceModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateDealPipeline = createAsyncThunk<string, SimpleReferenceModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/UpdateDealPipeline',
    async (model: CategoryModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/dealsPipelineType/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as string;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateDealPipeline = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeactivateDealPipeline',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/dealsPipelineType/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as string;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateDealPipeline = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/ReactivateDealPipeline',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/dealsPipelineType/reactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as string;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteDealPipeline = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeleteDealPipeline',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/dealsPipelineType/delete?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as string;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateDealPipeline = createAsyncThunk<string, SimpleReferenceModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/CreateDealPipeline',
    async (model: SimpleReferenceModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/dealsPipelineType/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

//#region Articles

export const GetActiveArticles = createAsyncThunk<ArticleModel[], void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetActiveArticles',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/article/getallactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ArticleModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInactiveArticles = createAsyncThunk<ArticleModel[], void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetInactiveArticles',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/article/getallinactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ArticleModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetArticle = createAsyncThunk<ArticleModel, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetArticle',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/article/get?articleId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getResponse = await response.json() as ArticleModel;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateArticle = createAsyncThunk<MainResponse, ArticleModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/CreateArticle',
    async (model: ArticleModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/article/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as MainResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateArticle = createAsyncThunk<MainResponse, ArticleModel, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/UpdateArticle',
    async (model: ArticleModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/article/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as MainResponse;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateArticle = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeactivateArticle',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/article/deactivate?articleId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as MainResponse;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateArticle = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/ReactivateArticle',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/article/reactivate?articleId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as MainResponse;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#region DealPipeline

//#endregion

//#region Articles

export const DeleteArticle = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/DeleteArticle',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/article/delete?articleId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as MainResponse;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetArticleCreateData = createAsyncThunk<ArticleCreateData, void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetArticleCreateData',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/article/create-data', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ArticleCreateData;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#endregion

//#region Shared 

export const GetAllCountries = createAsyncThunk<{ countries: ListModel<number>[] }, void, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetAllCountries',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/countries/all', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as { countries: ListModel<number>[] };
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPartnersForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetPartnersForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetContactsForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/ReferenceDataSlice/GetContactsForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

// PUT /api/travelAllowence/reactivate
export const ReactivateTravelAllowance = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/ReactivateTravelAllowance',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travelAllowence/reactivate?id=' + request, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// PUT /api/travelAllowence/deactivate
export const DeactivateTravelAllowance = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/DeactivateTravelAllowance',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travelAllowence/deactivate?id=' + request, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// PUT /api/travelAllowence/delete
export const DeleteTravelAllowance = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/DeleteTravelAllowance',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travelAllowence/delete?id=' + request, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// POST /api/travelAllowence/create
export const CreateTravelAllowance = createAsyncThunk<MainResponse, TravelAllowance, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/CreateTravelAllowance',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travelAllowence/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// POST /api/travelAllowence/update
export const UpdateTravelAllowance = createAsyncThunk<MainResponse, TravelAllowance, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/UpdateTravelAllowance',
    async (request, { rejectWithValue }) => {
        // The update endpoint binds the translations under `translations` (create uses
        // `dayTypeOrDurationTranslations`). Send both so the value binds regardless.
        const body = { ...request, translations: request.dayTypeOrDurationTranslations };
        const response = await fetch(baseUri + 'api/travelAllowence/update', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(body)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET /api/travelAllowence/getAll
export const GetAllTravelAllowances = createAsyncThunk<TravelAllowanceList, void, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetAllTravelAllowances',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travelAllowence/getAll', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TravelAllowanceList;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET /api/travelAllowence/getAllInactive
export const GetAllInactiveTravelAllowances = createAsyncThunk<TravelAllowanceList, void, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetAllInactiveTravelAllowances',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travelAllowence/getAllInactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TravelAllowanceList;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET /api/travelAllowence/get
export const GetTravelAllowance = createAsyncThunk<TravelAllowance, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetTravelAllowance',
    async (id, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travelAllowence/get?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TravelAllowance;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET /api/travelAllowence/getCreateData
export const GetTravelAllowanceCreateData = createAsyncThunk<TravelAllowanceCreateData, void, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetTravelAllowanceCreateData',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travelAllowence/getCreateData', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TravelAllowanceCreateData;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// PUT /api/driverTravelAllowance/reactivate
export const ReactivateDriverTravelAllowance = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/ReactivateDriverTravelAllowance',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/driverTravelAllowance/reactivate?id=' + request, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// PUT /api/driverTravelAllowance/deactivate ZASTO OVDE IMA SAMO ID U RESPONSE, A NE MAINRESPONSE KAO OSTALI DELETE I UPDATE?
export const DeactivateDriverTravelAllowance = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/DeactivateDriverTravelAllowance',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/driverTravelAllowance/deactivate?id=' + request, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// PUT /api/driverTravelAllowance/delete
export const DeleteDriverTravelAllowance = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/DeleteDriverTravelAllowance',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/driverTravelAllowance/delete?id=' + request, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// POST /api/driverTravelAllowance/create
export const CreateDriverTravelAllowance = createAsyncThunk<MainResponse, DriverTravelAllowance, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/CreateDriverTravelAllowance',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/driverTravelAllowance/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// POST /api/driverTravelAllowance/update
export const UpdateDriverTravelAllowance = createAsyncThunk<MainResponse, DriverTravelAllowance, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/UpdateDriverTravelAllowance',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/driverTravelAllowance/update', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET /api/driverTravelAllowance/getAll
export const GetAllDriverTravelAllowances = createAsyncThunk<DriverTravelAllowanceList, void, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetAllDriverTravelAllowances',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/driverTravelAllowance/getAll', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DriverTravelAllowanceList;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET /api/driverTravelAllowance/getAllInactive
export const GetAllInactiveDriverTravelAllowances = createAsyncThunk<DriverTravelAllowance[], void, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetAllInactiveDriverTravelAllowances',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/driverTravelAllowance/getAllInactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DriverTravelAllowance[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET /api/driverTravelAllowance/get
export const GetDriverTravelAllowance = createAsyncThunk<DriverTravelAllowance, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetDriverTravelAllowance',
    async (id, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/driverTravelAllowance/get?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DriverTravelAllowance;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET /api/driverTravelAllowance/getCreateData
export const GetDriverTravelAllowanceCreateData = createAsyncThunk<DriverTravelAllowanceCreateData, void, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetDriverTravelAllowanceCreateData',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/driverTravelAllowance/getCreateData', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DriverTravelAllowanceCreateData;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ReferenceDataSlice = createSlice({
    name: "ReferenceDataSlice",
    initialState: initalValue,
    reducers: {
        resetReferenceDataSlice: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
            state.isLoading = false;
            state.isModalLoading = false;
            state.listOfEmployeePositions = [];
            state.employeePosition = initEmployeePositionModel;
            state.listOfParticipantRoles = [];
            state.participantRole = initParticipantRoleModel;
            state.listOfSources = [];
            state.source = initSourceModel;
            state.listOfCateogires = [];
            state.category = initCategoryModel;
            state.listOfPaymentMethods = [];
            state.paymentMethod = initPaymentMethodModel;
            state.listOfIndustrySectors = [];
            state.industrySector = initSimpleReferenceModel;
            state.listOfPartnerTypes = [];
            state.partnerType = initSimpleReferenceModel;
            state.listOfEducationTypes = [];
            state.educationType = initSimpleReferenceModel;
            state.listOfDealPipelines = [];
            state.dealPipeline = initSimpleReferenceModel;
            state.listOfUnits = [];
            state.unit = initUnitModel;
            state.listOfAccountingNumbers = [];
            state.accountingNumber = initAccountingNumberModel;
            state.article = null;
            state.listOfActiveTravelAllowances = [];
            state.listOfInactiveTravelAllowances = [];
            state.travelAllowance = null;
            state.travelAllowanceCreateData = null;
            state.listOfActiveDriverTravelAllowances = [];
            state.listOfInactiveDriverTravelAllowances = [];
            state.driverTravelAllowance = null;
            state.driverTravelAllowanceCreateData = null;
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
        resetEmployeePosition: (state) => {
            state.isModalLoading = false;
            state.employeePosition = initEmployeePositionModel;
        },
        resetUnit: (state) => {
            state.isModalLoading = false;
            state.unit = initUnitModel;
        },
        resetSource: (state) => {
            state.isModalLoading = false;
            state.source = initSourceModel;
        },
        resetAccountingNumber: (state) => {
            state.isModalLoading = false;
            state.accountingNumber = initAccountingNumberModel;
        },
        resetCategory: (state) => {
            state.isModalLoading = false;
            state.category = initCategoryModel;
        },
        resetPaymentMethod: (state) => {
            state.isModalLoading = false;
            state.paymentMethod = initPaymentMethodModel;
        },
        resetParticipantRole: (state) => {
            state.isModalLoading = false;
            state.participantRole = initParticipantRoleModel;
        },
        resetIndustrySector: (state) => {
            state.isModalLoading = false;
            state.industrySector = initSimpleReferenceModel;
        },
        resetPartnerType: (state) => {
            state.isModalLoading = false;
            state.partnerType = initSimpleReferenceModel;
        },
        resetArticle: (state) => {
            state.isModalLoading = false;
            state.article = null;
        },
        resetDealPipeline: (state) => {
            state.isModalLoading = false;
            state.dealPipeline = null;
        },
        resetEducationType: (state) => {
            state.isModalLoading = false;
            state.educationType = null;
        },
        resetAbsenceType: (state) => {
            state.isModalLoading = false;
            state.absenceType = null;
        },
        setArticleSelectedPartners: (state, action: PayloadAction<ListModel<string>[]>) => {
            if (state.article) {
                state.article.selectedPartners = action.payload
            }
        },
        resetTravelAllowance: (state) => {
            state.isModalLoading = false;
            state.travelAllowance = null;
        },
        resetDriverTravelAllowance: (state) => {
            state.isModalLoading = false;
            state.driverTravelAllowance = null;
        },
        setArticleSelectedContacts: (state, action: PayloadAction<ListModel<string>[]>) => {
            if (state.article) {
                state.article.selectedContacts = action.payload
            }
        },
    },
    extraReducers(builder) {
        //#region Employee Positions

        //Get Employee Positions
        builder.addCase(GetAllEmployeePositions.fulfilled, (state, action: PayloadAction<EmployeePositionModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfEmployeePositions = action.payload;
        })
            .addCase(GetAllEmployeePositions.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllEmployeePositions.pending, state => {
                state.isLoading = true;
            })
        //GetEmployeePosition
        builder.addCase(GetEmployeePosition.fulfilled, (state, action: PayloadAction<EmployeePositionModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.employeePosition = action.payload;
        })
            .addCase(GetEmployeePosition.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetEmployeePosition.pending, state => {
                state.isModalLoading = true;
            })
        //UpdateEmployeePosition
        builder.addCase(UpdateEmployeePosition.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The employee position is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateEmployeePosition.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateEmployeePosition.pending, state => {
                state.isModalLoading = true;
            })
        //DeactivateEmployeePosition
        builder.addCase(DeactivateEmployeePosition.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The employee position is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivateEmployeePosition.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateEmployeePosition.pending, state => {
                state.isModalLoading = true;
            })
        //ReactivateEmployeePosition
        builder.addCase(ReactivateEmployeePosition.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The employee position is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivateEmployeePosition.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateEmployeePosition.pending, state => {
                state.isModalLoading = true;
            })
        //DeleteEmployeePosition
        builder.addCase(DeleteEmployeePosition.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The employee position is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeleteEmployeePosition.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteEmployeePosition.pending, state => {
                state.isModalLoading = true;
            })
        //CreateEmployeePosition
        builder.addCase(CreateEmployeePosition.fulfilled, (state, action: PayloadAction<string>) => {
            state.message = 'The employee position is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreateEmployeePosition.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateEmployeePosition.pending, state => {
                state.isModalLoading = true;
            })
        //#endregion

        //#region Units

        //Get Units
        builder.addCase(GetAllUnits.fulfilled, (state, action: PayloadAction<UnitModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfUnits = action.payload.sort((a, b) => a.name.localeCompare(b.name));
        })
            .addCase(GetAllUnits.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllUnits.pending, state => {
                state.isLoading = true;
            })
        //GetUnit
        builder.addCase(GetUnit.fulfilled, (state, action: PayloadAction<UnitModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.unit = action.payload;
        })
            .addCase(GetUnit.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetUnit.pending, state => {
                state.isModalLoading = true;
            })
        //UpdateUnit
        builder.addCase(UpdateUnit.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The unit is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateUnit.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateUnit.pending, state => {
                state.isModalLoading = true;
            })
        //DeactivateUnit
        builder.addCase(DeactivateUnit.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The unit is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivateUnit.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateUnit.pending, state => {
                state.isModalLoading = true;
            })
        //ReactivateUnit
        builder.addCase(ReactivateUnit.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The unit is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivateUnit.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateUnit.pending, state => {
                state.isModalLoading = true;
            })
        //DeleteUnit
        builder.addCase(DeleteUnit.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The unit is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeleteUnit.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteUnit.pending, state => {
                state.isModalLoading = true;
            })
        //CreateUnit
        builder.addCase(CreateUnit.fulfilled, (state, action: PayloadAction<string>) => {
            state.message = 'The unit is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreateUnit.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateUnit.pending, state => {
                state.isModalLoading = true;
            })
        //#endregion

        //#endregion

        //#endregion

        //#region Sources

        //Get Sources
        builder.addCase(GetAllSources.fulfilled, (state, action: PayloadAction<SourceModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfSources = action.payload;
        })
            .addCase(GetAllSources.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllSources.pending, state => {
                state.isLoading = true;
            })
        //GetSource
        builder.addCase(GetSource.fulfilled, (state, action: PayloadAction<SourceModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.source = action.payload;
        })
            .addCase(GetSource.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetSource.pending, state => {
                state.isModalLoading = true;
            })
        //UpdateSource
        builder.addCase(UpdateSource.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The source is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateSource.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateSource.pending, state => {
                state.isModalLoading = true;
            })
        //DeactivateSource
        builder.addCase(DeactivateSource.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The source is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivateSource.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateSource.pending, state => {
                state.isModalLoading = true;
            })
        //ReactivateSource
        builder.addCase(ReactivateSource.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The source is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivateSource.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateSource.pending, state => {
                state.isModalLoading = true;
            })
        //DeleteEmployeePosition
        builder.addCase(DeleteSource.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The source is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeleteSource.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteSource.pending, state => {
                state.isModalLoading = true;
            })
        //CreateSource
        builder.addCase(CreateSource.fulfilled, (state, action: PayloadAction<string>) => {
            state.message = 'The source is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreateSource.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateSource.pending, state => {
                state.isModalLoading = true;
            })
        //Get Source create data
        builder
            .addCase(GetSourceCreateData.fulfilled, (state, action: PayloadAction<{ sourceTypes: ListModel<number>[] }>) => {
                state.isLoading = false;
                state.message = '';
                state.createData.sourceTypes = action.payload.sourceTypes;
            })
            .addCase(GetSourceCreateData.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetSourceCreateData.pending, state => {
                state.isLoading = true;
            })

        //#endregion

        //#region Category cases

        //GetCategories
        builder.addCase(GetAllCategories.fulfilled, (state, action: PayloadAction<CategoryModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfCateogires = action.payload;
        })
            .addCase(GetAllCategories.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllCategories.pending, state => {
                state.isLoading = true;
            })

        //GetCategory
        builder.addCase(GetCategory.fulfilled, (state, action: PayloadAction<CategoryModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.category = action.payload;
        })
            .addCase(GetCategory.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetCategory.pending, state => {
                state.isModalLoading = true;
            })

        //UpdateCategory
        builder.addCase(UpdateCategory.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The category is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateCategory.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateCategory.pending, state => {
                state.isModalLoading = true;
            })

        //DeactivateCategory
        builder.addCase(DeactivateCategory.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The category is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivateCategory.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateCategory.pending, state => {
                state.isModalLoading = true;
            })

        //ReactivateCategory
        builder.addCase(ReactivateCategory.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The category is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivateCategory.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateCategory.pending, state => {
                state.isModalLoading = true;
            })

        //DeleteCategory
        builder.addCase(DeleteCategory.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The category is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeleteCategory.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteCategory.pending, state => {
                state.isModalLoading = true;
            })

        //CreateCategory
        builder.addCase(CreateCategory.fulfilled, (state, action: PayloadAction<string>) => {
            state.message = 'The category is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreateCategory.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateCategory.pending, state => {
                state.isModalLoading = true;
            })

        //#endregion

        //#region Payment method cases

        //GetAllPaymentMethods
        builder.addCase(GetAllPaymentMethods.fulfilled, (state, action: PayloadAction<PaymentMethodModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfPaymentMethods = action.payload;
        })
            .addCase(GetAllPaymentMethods.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllPaymentMethods.pending, state => {
                state.isLoading = true;
            })

        //GetPaymentMethod
        builder.addCase(GetPaymentMethod.fulfilled, (state, action: PayloadAction<PaymentMethodModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.paymentMethod = action.payload;
        })
            .addCase(GetPaymentMethod.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetPaymentMethod.pending, state => {
                state.isModalLoading = true;
            })

        //UpdatePaymentMethod
        builder.addCase(UpdatePaymentMethod.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The payment method successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdatePaymentMethod.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdatePaymentMethod.pending, state => {
                state.isModalLoading = true;
            })

        //DeactivatePaymentMethod
        builder.addCase(DeactivatePaymentMethod.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The payment method successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivatePaymentMethod.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivatePaymentMethod.pending, state => {
                state.isModalLoading = true;
            })

        //ReactivatePaymentMethod
        builder.addCase(ReactivatePaymentMethod.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The payment method successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivatePaymentMethod.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivatePaymentMethod.pending, state => {
                state.isModalLoading = true;
            })

        //DeletePaymentMethod
        builder.addCase(DeletePaymentMethod.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The payment method successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeletePaymentMethod.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeletePaymentMethod.pending, state => {
                state.isModalLoading = true;
            })

        //CreatePaymentMethod
        builder.addCase(CreatePaymentMethod.fulfilled, (state, action: PayloadAction<string>) => {
            state.message = 'The payment method successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreatePaymentMethod.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreatePaymentMethod.pending, state => {
                state.isModalLoading = true;
            })

        //#endregion

        //#region Accounting Numbers

        //Get Accounting Numbers
        builder.addCase(GetAllAccountingNumbers.fulfilled, (state, action: PayloadAction<AccountingNumberModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfAccountingNumbers = action.payload;
        })
            .addCase(GetAllAccountingNumbers.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllAccountingNumbers.pending, state => {
                state.isLoading = true;
            })
        //Get Accounting Number
        builder
            .addCase(GetAccountingNumber.fulfilled, (state, action: PayloadAction<AccountingNumberModel>) => {
                state.isModalLoading = false;
                state.message = '';
                state.accountingNumber = action.payload;
            })
            .addCase(GetAccountingNumber.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAccountingNumber.pending, state => {
                state.isModalLoading = true;
            })
        //CreateAccountingNumber
        builder
            .addCase(CreateAccountingNumber.fulfilled, (state, action: PayloadAction<string>) => {
                state.message = 'The accounting number is successfully created.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
            })
            .addCase(CreateAccountingNumber.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateAccountingNumber.pending, state => {
                state.isModalLoading = true;
            })
        //UpdateAccountingNumber
        builder
            .addCase(UpdateAccountingNumber.fulfilled, (state, action: PayloadAction<string>) => {
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = 'The accounting number is successfully updated.';
                state.showNotification = true;
            })
            .addCase(UpdateAccountingNumber.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateAccountingNumber.pending, state => {
                state.isModalLoading = true;
            })
        //DeactivateAccountingNumber
        builder
            .addCase(DeactivateAccountingNumber.fulfilled, (state, action: PayloadAction<string>) => {
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = 'The accounting number is successfully deactivated.';
                state.showNotification = true;
            })
            .addCase(DeactivateAccountingNumber.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateAccountingNumber.pending, state => {
                state.isModalLoading = true;
            })
        //ReactivateAccountingNumber
        builder
            .addCase(ReactivateAccountingNumber.fulfilled, (state, action: PayloadAction<string>) => {
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = 'The accounting number is successfully reactivated.';
                state.showNotification = true;
            })
            .addCase(ReactivateAccountingNumber.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateAccountingNumber.pending, state => {
                state.isModalLoading = true;
            })
        //DeleteAccountingNumber
        builder
            .addCase(DeleteAccountingNumber.fulfilled, (state, action: PayloadAction<string>) => {
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = 'The accounting number is successfully deleted.';
                state.showNotification = true;
            })
            .addCase(DeleteAccountingNumber.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteAccountingNumber.pending, state => {
                state.isModalLoading = true;
            })
        //#endregion

        //#region Partner type cases

        //GetPartnerTypes
        builder.addCase(GetAllPartnerTypes.fulfilled, (state, action: PayloadAction<SimpleReferenceModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfPartnerTypes = action.payload.map((item: any) => ({
                ...item,
                disableAction: item.name === 'Bank' || item.name === 'Banka' ? true : item.disableAction ?? false,
            }));
        })
            .addCase(GetAllPartnerTypes.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllPartnerTypes.pending, state => {
                state.isLoading = true;
            })

        //GetPartnerType
        builder.addCase(GetPartnerType.fulfilled, (state, action: PayloadAction<PartnerTypeModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.partnerType = action.payload;
        })
            .addCase(GetPartnerType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetPartnerType.pending, state => {
                state.isModalLoading = true;
            })

        //UpdatePartnerType
        builder.addCase(UpdatePartnerType.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The company type is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdatePartnerType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdatePartnerType.pending, state => {
                state.isModalLoading = true;
            })

        //DeactivatePartnerType
        builder.addCase(DeactivatePartnerType.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The company type is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivatePartnerType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivatePartnerType.pending, state => {
                state.isModalLoading = true;
            })

        //ReactivatePartnerType
        builder.addCase(ReactivatePartnerType.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The company type is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivatePartnerType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivatePartnerType.pending, state => {
                state.isModalLoading = true;
            })

        //DeletePartnerType
        builder.addCase(DeletePartnerType.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The company type is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeletePartnerType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeletePartnerType.pending, state => {
                state.isModalLoading = true;
            })

        //CreatePartnerType
        builder.addCase(CreatePartnerType.fulfilled, (state, action: PayloadAction<string>) => {
            state.message = 'The company type is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreatePartnerType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreatePartnerType.pending, state => {
                state.isModalLoading = true;
            })

        //#endregion

        //#region Absence type cases

        // get all absence types
        builder.addCase(GetAllAbsenceTypes.fulfilled, (state, action: PayloadAction<AbsenceTypeModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfAbsenceTypes = action.payload;
        })
            .addCase(GetAllAbsenceTypes.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllAbsenceTypes.pending, state => {
                state.isLoading = true;
            })

        // get absence type
        builder.addCase(GetAbsenceType.fulfilled, (state, action: PayloadAction<AbsenceTypeModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.absenceType = action.payload;
        })
            .addCase(GetAbsenceType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAbsenceType.pending, state => {
                state.isModalLoading = true;
            })

        // reactivate absence type    
        builder.addCase(ReactivateAbsenceType.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The absence type is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivateAbsenceType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateAbsenceType.pending, state => {
                state.isModalLoading = true;
            })

        // deactivate absence type    
        builder.addCase(DeactivateAbsenceType.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The absence type is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivateAbsenceType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeactivateAbsenceType.pending, state => {
                state.isModalLoading = true;
            })

        // delete absence type
        builder.addCase(DeleteAbsenceType.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The absence type is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeleteAbsenceType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteAbsenceType.pending, state => {
                state.isModalLoading = true;
            })

        // create absence type
        builder.addCase(CreateAbsenceType.fulfilled, (state, action: PayloadAction<string>) => {
            state.message = 'The absence type is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreateAbsenceType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateAbsenceType.pending, state => {
                state.isModalLoading = true;
            })

        // update absence type
        builder.addCase(UpdateAbsenceType.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The absence type is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateAbsenceType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateAbsenceType.pending, state => {
                state.isModalLoading = true;
            })

        //#endregion

        //#region Industry sector cases

        //GetIndustrySectors
        builder.addCase(GetAllIndustrySectors.fulfilled, (state, action: PayloadAction<SimpleReferenceModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfIndustrySectors = action.payload;
        })
            .addCase(GetAllIndustrySectors.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllIndustrySectors.pending, state => {
                state.isLoading = true;
            })

        //GetIndustrySector
        builder.addCase(GetIndustrySector.fulfilled, (state, action: PayloadAction<SimpleReferenceModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.industrySector = action.payload;
        })
            .addCase(GetIndustrySector.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetIndustrySector.pending, state => {
                state.isModalLoading = true;
            })

        //UpdateIndustrySector
        builder.addCase(UpdateIndustrySector.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The industry sector is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateIndustrySector.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateIndustrySector.pending, state => {
                state.isModalLoading = true;
            })

        //DeactivateIndustrySector
        builder.addCase(DeactivateIndustrySector.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The industry sector is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivateIndustrySector.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateIndustrySector.pending, state => {
                state.isModalLoading = true;
            })

        //ReactivateIndustrySector
        builder.addCase(ReactivateIndustrySector.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The industry sector is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivateIndustrySector.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateIndustrySector.pending, state => {
                state.isModalLoading = true;
            })

        //DeleteIndustrySector
        builder.addCase(DeleteIndustrySector.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The industry sector is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeleteIndustrySector.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteIndustrySector.pending, state => {
                state.isModalLoading = true;
            })

        //CreateIndustrySector
        builder.addCase(CreateIndustrySector.fulfilled, (state, action: PayloadAction<string>) => {
            state.message = 'The industry sector is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreateIndustrySector.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateIndustrySector.pending, state => {
                state.isModalLoading = true;
            })

        //#endregion

        //#region Education type cases

        //GetEducationTypes
        builder.addCase(GetAllEducationTypes.fulfilled, (state, action: PayloadAction<GetEducationTypesResponse>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfEducationTypes = action.payload.educationTypes;
        })
            .addCase(GetAllEducationTypes.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllEducationTypes.pending, state => {
                state.isLoading = true;
            })

        //GetEducationType
        builder.addCase(GetEducationType.fulfilled, (state, action: PayloadAction<SimpleReferenceModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.educationType = action.payload;
        })
            .addCase(GetEducationType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetEducationType.pending, state => {
                state.isModalLoading = true;
            })

        //UpdateEducationType
        builder.addCase(UpdateEducationType.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The education type is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateEducationType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateEducationType.pending, state => {
                state.isModalLoading = true;
            })

        //DeactivateEducationType
        builder.addCase(DeactivateEducationType.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The education type is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivateEducationType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateEducationType.pending, state => {
                state.isModalLoading = true;
            })

        //ReactivateEducationType
        builder.addCase(ReactivateEducationType.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The education type is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivateEducationType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateEducationType.pending, state => {
                state.isModalLoading = true;
            })

        //DeleteEducationType
        builder.addCase(DeleteEducationType.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The education type is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeleteEducationType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteEducationType.pending, state => {
                state.isModalLoading = true;
            })

        //CreateEducationType
        builder.addCase(CreateEducationType.fulfilled, (state, action: PayloadAction<string>) => {
            state.message = 'The education type is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreateEducationType.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateEducationType.pending, state => {
                state.isModalLoading = true;
            })

        //#endregion

        //#region Deal pipeline cases

        //GetDealPipelines
        builder.addCase(GetAllDealPipelines.fulfilled, (state, action: PayloadAction<GetDealsPipelineTypesResponse>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfDealPipelines = action.payload.dealsPipelineTypes;
        })
            .addCase(GetAllDealPipelines.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllDealPipelines.pending, state => {
                state.isLoading = true;
            })

        //GetDealPipeline
        builder.addCase(GetDealPipeline.fulfilled, (state, action: PayloadAction<SimpleReferenceModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.dealPipeline = action.payload;
        })
            .addCase(GetDealPipeline.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetDealPipeline.pending, state => {
                state.isModalLoading = true;
            })

        //UpdateDealPipeline
        builder.addCase(UpdateDealPipeline.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The deal pipeline is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateDealPipeline.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateDealPipeline.pending, state => {
                state.isModalLoading = true;
            })

        //DeactivateDealPipeline
        builder.addCase(DeactivateDealPipeline.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The deal pipeline is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivateDealPipeline.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateDealPipeline.pending, state => {
                state.isModalLoading = true;
            })

        //ReactivateDealPipeline
        builder.addCase(ReactivateDealPipeline.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The deal pipeline is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivateDealPipeline.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateDealPipeline.pending, state => {
                state.isModalLoading = true;
            })

        //DeleteEDealPipeline
        builder.addCase(DeleteDealPipeline.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The deal pipeline is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeleteDealPipeline.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteDealPipeline.pending, state => {
                state.isModalLoading = true;
            })

        //CreateDealPipeline
        builder.addCase(CreateDealPipeline.fulfilled, (state, action: PayloadAction<string>) => {
            state.message = 'The deal pipeline is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreateDealPipeline.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateDealPipeline.pending, state => {
                state.isModalLoading = true;
            })

        //#endregion

        //#region Article cases

        //GetActiveArticles
        builder.addCase(GetActiveArticles.fulfilled, (state, action: PayloadAction<ArticleModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfActiveArticles = action.payload;
        })
            .addCase(GetActiveArticles.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetActiveArticles.pending, state => {
                state.isLoading = true;
            })
        //GetInactiveArticles
        builder.addCase(GetInactiveArticles.fulfilled, (state, action: PayloadAction<ArticleModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfInactiveArticles = action.payload;
        })
            .addCase(GetInactiveArticles.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetInactiveArticles.pending, state => {
                state.isLoading = true;
            })
        //GetArticle
        builder.addCase(GetArticle.fulfilled, (state, action: PayloadAction<ArticleModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.article = action.payload;
        })
            .addCase(GetArticle.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetArticle.pending, state => {
                state.isModalLoading = true;
            })

        //CreateArticle
        builder.addCase(CreateArticle.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.message = 'The article is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreateArticle.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateArticle.pending, state => {
                state.isModalLoading = true;
            })

        //UpdateArticle
        builder.addCase(UpdateArticle.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The article is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateArticle.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateArticle.pending, state => {
                state.isModalLoading = true;
            })
        //DeactivateArticle
        builder.addCase(DeactivateArticle.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The article is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivateArticle.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateArticle.pending, state => {
                state.isModalLoading = true;
            })
        //ReactivateArticle
        builder.addCase(ReactivateArticle.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The article is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivateArticle.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateArticle.pending, state => {
                state.isModalLoading = true;
            })
        //DeleteArticle
        builder.addCase(DeleteArticle.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The article is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeleteArticle.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteArticle.pending, state => {
                state.isModalLoading = true;
            })

        //Get article create data
        builder.addCase(GetArticleCreateData.fulfilled, (state, action: PayloadAction<ArticleCreateData>) => {
            state.isModalLoading = false;
            state.createData.projects = action.payload.projects;
            state.createData.articleTypes = action.payload.articleTypes;
        })
            .addCase(GetArticleCreateData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetArticleCreateData.pending, state => {
                state.isModalLoading = true;
            })

        //#endregion

        //#region Shared

        //Get All Countries
        builder
            .addCase(GetAllCountries.fulfilled, (state, action: PayloadAction<{ countries: ListModel<number>[] }>) => {
                state.isLoading = false;
                state.message = '';
                state.createData.countries = action.payload.countries.map(country => ({
                    ...country,
                    value: country.value !== null ? String(country.value) : null
                }));
            })
            .addCase(GetAllCountries.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllCountries.pending, state => {
                state.isLoading = true;
            })
        //#endregion

        //region Participant Roles

        //Get Participant Roles
        builder.addCase(GetAllParticipantRoles.fulfilled, (state, action: PayloadAction<ParticipantRoleModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfParticipantRoles = action.payload;
        })
            .addCase(GetAllParticipantRoles.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllParticipantRoles.pending, state => {
                state.isLoading = true;
            })
        //GetParticipantRole
        builder.addCase(GetParticipantRole.fulfilled, (state, action: PayloadAction<ParticipantRoleModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.participantRole = action.payload;
        })
            .addCase(GetParticipantRole.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetParticipantRole.pending, state => {
                state.isModalLoading = true;
            })
        //UpdateParticipantRole
        builder.addCase(UpdateParticipantRole.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'Participant role is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateParticipantRole.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateParticipantRole.pending, state => {
                state.isModalLoading = true;
            })
        //DeactivateParticipantRole
        builder.addCase(DeactivateParticipantRole.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'Participant role is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivateParticipantRole.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateParticipantRole.pending, state => {
                state.isModalLoading = true;
            })
        //ReactivateParticipantRole
        builder.addCase(ReactivateParticipantRole.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'Participant role is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivateParticipantRole.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateParticipantRole.pending, state => {
                state.isModalLoading = true;
            })
        //DeleteParticipantRole
        builder.addCase(DeleteParticipantRole.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'Participant role is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeleteParticipantRole.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteParticipantRole.pending, state => {
                state.isModalLoading = true;
            })
        //CreateParticipantRole
        builder.addCase(CreateParticipantRole.fulfilled, (state, action: PayloadAction<string>) => {
            state.message = 'Participant role is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreateParticipantRole.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateParticipantRole.pending, state => {
                state.isModalLoading = true;
            })
        builder.addCase(GetPartnersForDropdown.pending, (state) => {
            state.createData.filteredPartnersList.loading = true;
            state.createData.filteredPartnersList.error = null;
        })
            .addCase(GetPartnersForDropdown.fulfilled, (state, action) => {
                state.createData.filteredPartnersList.loading = false;
                state.createData.filteredPartnersList.data = action.payload.data;
                state.createData.filteredPartnersList.totalItems = action.payload.totalItems;
                state.createData.filteredPartnersList.totalPages = action.payload.totalPages;
                state.createData.filteredPartnersList.currentPage = action.payload.currentPage;
            })
            .addCase(GetPartnersForDropdown.rejected, (state, action) => {
                state.createData.filteredPartnersList.loading = false;
                state.createData.filteredPartnersList.error = action.payload?.message || 'Error fetching data';
            });

        //Get contacts list
        builder.addCase(GetContactsForDropdown.pending, (state) => {
            state.createData.filteredContactsList.loading = true;
            state.createData.filteredContactsList.error = null;
        })
            .addCase(GetContactsForDropdown.fulfilled, (state, action) => {
                state.createData.filteredContactsList.loading = false;
                state.createData.filteredContactsList.data = action.payload.data;
                state.createData.filteredContactsList.totalItems = action.payload.totalItems;
                state.createData.filteredContactsList.totalPages = action.payload.totalPages;
                state.createData.filteredContactsList.currentPage = action.payload.currentPage;
            })
            .addCase(GetContactsForDropdown.rejected, (state, action) => {
                state.createData.filteredContactsList.loading = false;
                state.createData.filteredContactsList.error = action.payload?.message || 'Error fetching data';
            });

        //#endregion

        //#region Travel Allowances

        //GetAllTravelAllowances
        builder
            .addCase(GetAllTravelAllowances.fulfilled, (state, action: PayloadAction<TravelAllowanceList>) => {
                state.isLoading = false;
                state.message = '';
                state.listOfActiveTravelAllowances = action.payload.travelAllowences;
            })
            .addCase(GetAllTravelAllowances.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAllTravelAllowances.pending, state => {
                state.isLoading = true;
            })

        //GetAllInactiveTravelAllowances
        builder.addCase(GetAllInactiveTravelAllowances.fulfilled, (state, action: PayloadAction<TravelAllowanceList>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfInactiveTravelAllowances = action.payload.travelAllowences;
        })
            .addCase(GetAllInactiveTravelAllowances.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAllInactiveTravelAllowances.pending, state => {
                state.isLoading = true;
            })

        //GetTravelAllowance
        builder.addCase(GetTravelAllowance.fulfilled, (state, action: PayloadAction<TravelAllowance>) => {
            state.isModalLoading = false;
            state.message = '';
            state.travelAllowance = action.payload;
        })
            .addCase(GetTravelAllowance.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTravelAllowance.pending, state => {
                state.isModalLoading = true;
            })

        //GetTravelAllowanceCreateData
        builder.addCase(GetTravelAllowanceCreateData.fulfilled, (state, action: PayloadAction<TravelAllowanceCreateData>) => {
            state.isModalLoading = false;
            state.travelAllowanceCreateData = action.payload;
        })
            .addCase(GetTravelAllowanceCreateData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTravelAllowanceCreateData.pending, state => {
                state.isModalLoading = true;
            })

        //CreateTravelAllowance
        builder.addCase(CreateTravelAllowance.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.message = 'The travel allowance is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreateTravelAllowance.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateTravelAllowance.pending, state => {
                state.isModalLoading = true;
            })

        //UpdateTravelAllowance
        builder.addCase(UpdateTravelAllowance.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The travel allowance is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateTravelAllowance.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateTravelAllowance.pending, state => {
                state.isModalLoading = true;
            })

        //DeactivateTravelAllowance
        builder.addCase(DeactivateTravelAllowance.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The travel allowance is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivateTravelAllowance.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateTravelAllowance.pending, state => {
                state.isModalLoading = true;
            })

        //ReactivateTravelAllowance
        builder.addCase(ReactivateTravelAllowance.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The travel allowance is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivateTravelAllowance.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateTravelAllowance.pending, state => {
                state.isModalLoading = true;
            })

        //DeleteTravelAllowance
        builder.addCase(DeleteTravelAllowance.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The travel allowance is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeleteTravelAllowance.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteTravelAllowance.pending, state => {
                state.isModalLoading = true;
            })

        //#endregion

        //#region Driver Travel Allowances

        //GetAllDriverTravelAllowances
        builder.addCase(GetAllDriverTravelAllowances.fulfilled, (state, action: PayloadAction<DriverTravelAllowanceList>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfActiveDriverTravelAllowances = action.payload.driverTravelAllowances;
        })
            .addCase(GetAllDriverTravelAllowances.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAllDriverTravelAllowances.pending, state => {
                state.isLoading = true;
            })

        //GetAllInactiveDriverTravelAllowances
        builder.addCase(GetAllInactiveDriverTravelAllowances.fulfilled, (state, action: PayloadAction<DriverTravelAllowance[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfInactiveDriverTravelAllowances = action.payload;
        })
            .addCase(GetAllInactiveDriverTravelAllowances.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAllInactiveDriverTravelAllowances.pending, state => {
                state.isLoading = true;
            })

        //GetDriverTravelAllowance
        builder.addCase(GetDriverTravelAllowance.fulfilled, (state, action: PayloadAction<DriverTravelAllowance>) => {
            state.isModalLoading = false;
            state.message = '';
            state.driverTravelAllowance = action.payload;
        })
            .addCase(GetDriverTravelAllowance.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetDriverTravelAllowance.pending, state => {
                state.isModalLoading = true;
            })

        //GetDriverTravelAllowanceCreateData
        builder.addCase(GetDriverTravelAllowanceCreateData.fulfilled, (state, action: PayloadAction<DriverTravelAllowanceCreateData>) => {
            state.isModalLoading = false;
            state.driverTravelAllowanceCreateData = action.payload;
        })
            .addCase(GetDriverTravelAllowanceCreateData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetDriverTravelAllowanceCreateData.pending, state => {
                state.isModalLoading = true;
            })

        //CreateDriverTravelAllowance
        builder.addCase(CreateDriverTravelAllowance.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.message = 'The driver travel allowance is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
        })
            .addCase(CreateDriverTravelAllowance.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateDriverTravelAllowance.pending, state => {
                state.isModalLoading = true;
            })

        //UpdateDriverTravelAllowance
        builder.addCase(UpdateDriverTravelAllowance.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The driver travel allowance is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateDriverTravelAllowance.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateDriverTravelAllowance.pending, state => {
                state.isModalLoading = true;
            })

        //DeactivateDriverTravelAllowance
        builder.addCase(DeactivateDriverTravelAllowance.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The driver travel allowance is successfully deactivated.';
            state.showNotification = true;
        })
            .addCase(DeactivateDriverTravelAllowance.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateDriverTravelAllowance.pending, state => {
                state.isModalLoading = true;
            })

        //ReactivateDriverTravelAllowance
        builder.addCase(ReactivateDriverTravelAllowance.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The driver travel allowance is successfully reactivated.';
            state.showNotification = true;
        })
            .addCase(ReactivateDriverTravelAllowance.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateDriverTravelAllowance.pending, state => {
                state.isModalLoading = true;
            })

        //DeleteDriverTravelAllowance
        builder.addCase(DeleteDriverTravelAllowance.fulfilled, (state, action: PayloadAction<MainResponse>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The driver travel allowance is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeleteDriverTravelAllowance.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteDriverTravelAllowance.pending, state => {
                state.isModalLoading = true;
            })

        //#endregion
    }

})

export const { resetReferenceDataSlice, resetStatusCode, resetEmployeePosition, resetParticipantRole, resetSource, resetUnit, resetEducationType,
    resetAccountingNumber, resetCategory, resetPaymentMethod, resetIndustrySector, resetPartnerType, resetDealPipeline,
    resetArticle, resetTravelAllowance, resetDriverTravelAllowance, setArticleSelectedPartners, setArticleSelectedContacts, resetAbsenceType } = ReferenceDataSlice.actions;
export default ReferenceDataSlice.reducer;